/**
 * Emerging World Models (EWM) — public narrative content.
 *
 * This module holds the *public* positioning content derived from the three
 * investor documents (Executive Summary, Business Brief, Technical Proposal).
 *
 * Editorial rules applied here (see docs/ gap analysis):
 *   - Every figure is traceable to a named proof-of-concept.
 *   - Caveats travel with the claims, next to the result.
 *   - Confidential material (the $10.0M ask, market sizing) is NOT published
 *     here — the internal documents state the sizing is directional and
 *     "will be validated before external use". It is not exposed on this site.
 */

/* ------------------------------------------------------------------ */
/*  The problem                                                       */
/* ------------------------------------------------------------------ */

export const PROBLEMS = [
  {
    id: 1,
    title: 'Bounded working context',
    body:
      'Reasoning quality is capped by what fits in one window. Everything relevant must be re-sent every turn, so cost and latency scale with it. When the window fills, something is dropped — heuristically, not structurally — and the model cannot tell you what it lost.',
  },
  {
    id: 2,
    title: 'Lossy, unstructured history',
    body:
      'Long-term memory arrives as summaries, notes or retrieved chunks. Summarization discards structure: counts, overlaps and novelty vanish. Retrieval is similarity, not position in a lattice, so there is no guarantee of coverage.',
  },
  {
    id: 3,
    title: 'No structural view at all',
    body:
      'You cannot ask a model\u2019s own output what is new, what is retained and what departed. There is no algebra over model state — only text diffs and embeddings. Nothing is content-addressed, so nothing is independently verifiable.',
  },
];

/* ------------------------------------------------------------------ */
/*  Why now                                                           */
/* ------------------------------------------------------------------ */

export const WHY_NOW = [
  {
    id: 1,
    title: 'Agents went to production',
    body:
      'Sessions run for hours or days, tools multiply, and context fragments across calls. Statelessness is an operational defect, not a design detail.',
  },
  {
    id: 2,
    title: 'Context is priced, not free',
    body:
      'Every long-context call re-bills the whole window. Context length — not output length — dominates inference cost and latency.',
  },
  {
    id: 3,
    title: 'Memory became the integration surface',
    body:
      'RAG, vector stores and note-taking agents: everyone is adding a memory layer, without a conservation law, a composition rule, or a way to verify it.',
  },
  {
    id: 4,
    title: 'Hardware reality bites',
    body:
      'Buyers want capability without a GPU-shaped bill. Anything that adds memory must be cheap, deterministic and deployable on-premise.',
  },
];

/* ------------------------------------------------------------------ */
/*  The side-car loop                                                 */
/* ------------------------------------------------------------------ */

export const LOOP_STAGES = [
  {
    id: 1,
    step: 'Encodings in',
    detail: 'Host encodings — f32 vectors, per step. No text round-trip, no prompt surgery.',
  },
  {
    id: 2,
    step: 'Encode',
    detail: 'Codebook quantizer — the only vocabulary-aware step in the whole loop.',
  },
  {
    id: 3,
    step: 'Ingest',
    detail: 'Hash to bits → HLLSets. A 32,768-site measurement plate.',
  },
  {
    id: 4,
    step: 'State',
    detail: 'S(t), H(t-1), D / R / N, and the GF(2) ring basis, per commit.',
  },
  {
    id: 5,
    step: 'Materialize',
    detail: 'Ordered restore via a De Bruijn walk; beam search when the graph is ambiguous.',
  },
  {
    id: 6,
    step: 'Back to host',
    detail: 'Restored ids → the host\u2019s own decoder or planner.',
  },
];

export const LOOP_PROPERTIES = [
  {
    title: 'It consumes measurements, not meaning',
    body:
      'The host\u2019s own internal encodings are the input — no text round-trip, no prompt re-engineering. Semantics stay in the host encoder and decoder.',
  },
  {
    title: 'It commits, it does not mutate',
    body:
      'A commit advances the tip and records the new state; the previous state stays valid and addressable forever. History is append-only.',
  },
  {
    title: 'It restores order, not just content',
    body:
      'Ordered materialization reconstructs the original sequence. Every candidate is kept; nothing is filtered away on a threshold.',
  },
];

/* ------------------------------------------------------------------ */
/*  The framework — four principles                                   */
/* ------------------------------------------------------------------ */

export const PRINCIPLES = [
  {
    id: 1,
    name: 'Emergent Ontology',
    statement:
      'Elements and subsystems are not primitive; they crystallize from accumulated measurement interactions.',
    buys:
      'A model-agnostic ontology: no fixed vocabulary, no frozen tokenizer, no re-architecture to learn something new.',
  },
  {
    id: 2,
    name: 'IICA Morphisms',
    statement:
      'Every relation is Idempotent, Immutable and Content-Addressed; composition preserves all three properties.',
    buys:
      'No consensus protocol, no versioning, no naming service — and arbitrary nesting of systems without new theory.',
  },
  {
    id: 3,
    name: 'Ashby\u2013Bootstrap',
    statement:
      'Requisite variety is met by multiplying measurements (n-grams, windows, modalities), never by reducing resolution.',
    buys:
      'One algebra for tokens, images and tensors: the same measurement machinery covers text, vision and multi-channel state.',
  },
  {
    id: 4,
    name: 'Noether Evolution',
    statement:
      'Every symmetry of the measurement regime yields a conserved quantity; structural change is redistribution, not accumulation.',
    buys:
      'The union invariant, the D/R/N conservation balance, holographic recall — guarantees, not heuristics.',
  },
];

/* ------------------------------------------------------------------ */
/*  Five guarantees                                                   */
/* ------------------------------------------------------------------ */

export const GUARANTEES = [
  {
    name: 'Idempotent',
    body:
      'The same input yields the same state, every time — A ∪ A = A, and hashing is deterministic. Replaying a turn is free and always safe.',
  },
  {
    name: 'Immutable',
    body:
      'A computed state never changes. "Version n+1" is simply a new state with a new content ID; the old one remains valid and addressable.',
  },
  {
    name: 'Content-addressed',
    body:
      'If a = b then h(a) = h(b). The hash is the name: no registry, no naming service, no version negotiation between components.',
  },
  {
    name: 'Composition theorem',
    body:
      'Any pipeline of IICA morphisms is itself an IICA morphism. Tokenizers, bridges, materializers and temporal aggregators compose without coordination — no consensus protocol, no leader election, no retry logic.',
  },
  {
    name: 'Union invariant (Noether)',
    body:
      'The union of all temporal layers is monotonic and convergent: information is never lost, only redistributed. Eventual consistency is a mathematical property here, not a protocol you must build.',
  },
];

/* ------------------------------------------------------------------ */
/*  State equation + the delta                                        */
/* ------------------------------------------------------------------ */

export const STATE_EQUATION = {
  formula: 'H(t) = ( S(t) , H(t-1) , D , R , N )',
  terms: [
    { symbol: 'S(t)', meaning: 'current emerging state (the tip)' },
    { symbol: 'H(t-1)', meaning: 'previous committed state (the cache)' },
    { symbol: 'N(t)', meaning: 'S(t) \\ H(t-1) — new' },
    { symbol: 'R(t)', meaning: 'S(t) ∩ H(t-1) — retained' },
    { symbol: 'D(t)', meaning: 'H(t-1) \\ S(t) — departed' },
  ],
  identities: [
    'D ∪ R = H(t-1)',
    'R ∪ N = S(t)',
    'D ∩ N = ∅',
    '|card(N) − card(D)| → 0',
  ],
};

export const DELTA = [
  {
    dimension: 'Working context',
    today: 'A fixed window, re-sent every call; overflow is dropped heuristically.',
    ewm: 'Structural state outside the model, content-addressed, not bounded by the window.',
  },
  {
    dimension: 'Change detection',
    today: 'Text diff or a summary; "what changed?" has no precise answer.',
    ewm: 'D / R / N per commit — departed, retained, novel — with a conservation balance.',
  },
  {
    dimension: 'Recall',
    today: 'Summaries and retrieved chunks; structure is lost in the process.',
    ewm: 'Holographic: the lattice top plus the frequency stack reconstructs past states.',
  },
  {
    dimension: 'Cost profile',
    today: 'Grows with context length on every call.',
    ewm: 'Bit operations on a fixed 32,768-site plate — CPU-class, flat as usage grows.',
  },
  {
    dimension: 'Portability',
    today: 'Memory is coupled to one model family, vendor and version.',
    ewm: 'Measurement-level and model-agnostic: the same lattice spans hosts and modalities.',
  },
];

/* ------------------------------------------------------------------ */
/*  Proof of concept — the six measured scenarios                     */
/* ------------------------------------------------------------------ */

export const EVIDENCE = [
  {
    id: 1,
    scenario: 'VLA side-car',
    host: 'Qwen-Drive-1.0 perception tokens, 48 camera frames',
    measured:
      'Round-trip of the host\u2019s own encodings through the side-car, per frame.',
    result: 'Loop accuracy 1.0 · set recall 1.0 / precision 1.0',
    source: 'ewm-state-machine notebook 03_qwendrive_sidecar.ipynb',
  },
  {
    id: 2,
    scenario: 'Scene side-car (2-D)',
    host: '16×16 vision patch grids, conv(n,2)',
    measured:
      'Positional restoration (greedy and beam-2) and similarity against the host.',
    result: 'Positional accuracy 1.0 · r(cosine, BSS) = 0.935',
    source: '02_scene_sidecar.ipynb and 04_perceptron_pyramid.ipynb',
  },
  {
    id: 3,
    scenario: 'OCR semantic compressor',
    host: 'DeepSeek-OCR, real 128K-vocabulary BPE ids',
    measured:
      'Tokenize → hash → gate → lookup table → ordered reconstruction.',
    result: '100% word retention, zero order loss',
    source: 'hllset-cortex validation — DeepSeek-OCR',
  },
  {
    id: 4,
    scenario: 'Perceptron pyramid',
    host: 'Perception, planner, ego and navigation states',
    measured:
      'Union of component states vs. concatenated ingest — same content key?',
    result: 'Identical keys: nodes are discovered, not created',
    source: '04_perceptron_pyramid.ipynb',
  },
  {
    id: 5,
    scenario: 'Integrated platform',
    host: 'NLP Q&A, OCR, clinical and invoice documents',
    measured:
      'Deployment on commodity CPU hardware with content-addressed storage.',
    result: 'No GPU · ~2.5 GB idle · 55k+ card corpus',
    source: 'Guru-EWM platform proof-of-concept',
  },
  {
    id: 6,
    scenario: 'Driving-rule agent',
    host: 'Text-only rule induction (no gradient descent)',
    measured:
      'Scenario accuracy on driving decisions learned from a short rule brief.',
    result: '80% scenario accuracy from 10 sentences',
    source: 'Rule-agent evaluation',
  },
];

export const EVIDENCE_CAVEATS = [
  'Perfect retention is measured at hash and order level. Perceptual quality of the final document image remains a host-side question.',
  'Clinical outputs are research demonstrations, not a medical device, and do not replace clinician review.',
  'The exact release number of the host checkpoint does not matter; what matters is the encoding interface, which is stable across the host family.',
];

export const SCALE_FACTS = [
  { value: '12', label: 'crates in the EWM workspace' },
  { value: '240', label: 'automated tests in the repository' },
  { value: '32,768', label: 'measurement sites per state, content-addressed' },
  { value: '0', label: 'model changes — no retraining, no fine-tuning, no new weights' },
];

export const ALGEBRA_OPERATIONS = [
  { op: '∪ ∩ \\ Δ', meaning: 'union · intersection · difference · symmetric difference (GF(2) addition)' },
  { op: 'popcount', meaning: 'cardinality of any state, without materializing tokens' },
  { op: 'gates Gₙ ∩ H', meaning: 'extract the n-window channel of any state — dimension-agnostic' },
  { op: 'BSS', meaning: '|A ∩ B| / |B| — a deterministic similarity, defined for any state' },
  { op: 'GF(2) coordinates', meaning: 'exact decomposition over a Boolean-ring basis; residual = frame-relative novelty' },
];

export const ARCHITECTURE_LAYERS = [
  { name: 'hllset-core', role: 'the bit plane: union, intersection, difference, popcount, SHA-1' },
  { name: 'hllset-contracts', role: 'soldered invariants — the leaf everything depends on' },
  { name: 'hllset-cid', role: 'embedded content identifiers' },
  { name: 'hllset-lut', role: 'the lookup-table lattice: token interpretation' },
  { name: 'hllset-morphisms', role: 'ingest / materialize and the default application API' },
  { name: 'hllset-storage', role: 'content-addressed stores (memory + sled)' },
  { name: 'context-tree', role: 'Merkle tree over the working set S(t)' },
  { name: 'ewm-boolring', role: 'GF(2) context index: hard keys and soft keys' },
  { name: 'ewm-git', role: 'the state stack: commit DAG, tip, recovery' },
  { name: 'ewm-app', role: 'the processing unit: stateless driver + shareable state cache' },
  { name: 'ewm-scene', role: 'side-car bench and CLI for scene and tensor encodings' },
  { name: 'ewm-sm-explore', role: 'read-only explorer over the committed state' },
];

/* ------------------------------------------------------------------ */
/*  Industries — where the wedge lands first                          */
/* ------------------------------------------------------------------ */

export const INDUSTRIES = [
  {
    id: 1,
    icon: '🏥',
    name: 'Healthcare & clinical documents',
    body:
      'Confidential records cannot leave the premises, and generated answers are a liability. Report triage and document matching run inside the customer boundary.',
    note: 'Clinical outputs are research demonstrations, not a medical device.',
  },
  {
    id: 2,
    icon: '⚖️',
    name: 'Legal, insurance & finance',
    body:
      'Regulated document verticals with the highest willingness to pay and the hardest privacy constraint — which is exactly the constraint the side-car satisfies.',
  },
  {
    id: 3,
    icon: '🤖',
    name: 'Agent infrastructure',
    body:
      'Long-running sessions across many tools and models need memory that is measurable and portable — not a per-vendor note store.',
  },
  {
    id: 4,
    icon: '🏭',
    name: 'On-premise, edge & cost-sensitive mid-market',
    body:
      'Sovereignty and data-residency rules push inference inside the customer boundary, where GPUs and cloud calls are not available. Buyers priced out of GPU clouds and per-token fees need capability on commodity hardware with a flat cost curve.',
  },
];

/* ------------------------------------------------------------------ */
/*  Vertical packs                                                    */
/* ------------------------------------------------------------------ */

export const VERTICAL_PACKS = [
  {
    id: 1,
    icon: '🩺',
    name: 'Clinical report triage',
    body:
      'ECG, X-ray, CT, knee-MRI and lab reports matched against finding cards with numeric reference ranges.',
    status: 'In development',
  },
  {
    id: 2,
    icon: '🧾',
    name: 'Invoice & document extraction',
    body:
      'Image and PDF text extraction on commodity CPUs, then template-level field extraction for financial documents.',
    status: 'In development',
  },
  {
    id: 3,
    icon: '🧠',
    name: 'Agent memory',
    body:
      'Structural memory for long-horizon agent sessions: recall, novelty and drift computed on every commit.',
    status: 'In development',
  },
];

/* ------------------------------------------------------------------ */
/*  Positioning — against the alternatives                            */
/* ------------------------------------------------------------------ */

export const COMPARISON = [
  {
    approach: 'Retrieval & vector stores',
    holds: 'Embedded chunks in an index',
    guarantees: 'Similarity ranking only — no coverage or conservation law',
    coupling: 'Tied to an embedding model and its version',
    cost: 'Index plus context grow together',
  },
  {
    approach: 'LLM-written memory',
    holds: 'Notes, summaries, extracted facts',
    guarantees: 'None: output depends on the writer model',
    coupling: 'Tied to one model family and its prompt',
    cost: 'Grows with every write and read',
  },
  {
    approach: 'Long context / fine-tuning',
    holds: 'The whole window, or weights',
    guarantees: 'None: behavior is stochastic and opaque',
    coupling: 'Bound to one checkpoint',
    cost: 'Grows with window length on every call',
  },
  {
    approach: 'EWM side-car',
    holds: 'Content-addressed measurement states',
    guarantees: 'IICA properties plus a monotonic union invariant',
    coupling: 'Measurement-level: the same state across hosts',
    cost: 'Bit operations on a fixed plate — CPU-class',
    highlight: true,
  },
];

/* ------------------------------------------------------------------ */
/*  Published record                                                  */
/* ------------------------------------------------------------------ */

export const PUBLICATIONS = [
  {
    id: 1,
    title:
      'Emerging World Models — A Structural Framework for Self-Organizing World Representations',
    venue: 'Consolidation document, August 2026 (Kumar & Mylnikov)',
    type: 'Framework document',
    note:
      'The four principles, their HLLSet realization, the Noether evolution law and the Grothendieck-topology target.',
  },
  {
    id: 2,
    title: 'Emerging World Models',
    venue: 'IJESIRD Vol. 13, Issue 2 (August 2026)',
    type: 'Journal publication',
    note: 'The published record behind the structural framework.',
  },
  {
    id: 3,
    title: 'HLLSet research program',
    venue: 'ASTESJ 2026',
    type: 'Journal publication',
    note: 'The HLLSet Algebra program — the bit-plane foundation the framework is built on.',
  },
  {
    id: 4,
    title: 'HLLSet foundations',
    venue: 'SGS 2024',
    type: 'Publication',
    note: 'Earlier HLLSet result, unified by the framework into the four principles.',
  },
];

export const PUBLISHED_REPOS = [
  'ewm-state-machine notebooks 01–04 (ingest/materialize, scene side-car, Qwen-Drive side-car, perceptron pyramid)',
  'hllset-cortex validation (DeepSeek-OCR)',
  'the Guru-EWM platform proof-of-concepts',
];

/* ------------------------------------------------------------------ */
/*  Trust & compliance                                                */
/* ------------------------------------------------------------------ */

export const TRUST_POINTS = [
  {
    icon: '🔒',
    title: 'Nothing upstream changes',
    body:
      'The host keeps its weights, tokenizer, prompts and APIs. The side-car consumes the host\u2019s internal encodings and returns encodings — no text round-trip required.',
  },
  {
    icon: '🛡️',
    title: 'Your data stays inside your boundary',
    body:
      'The memory lives outside the model, in storage you control. We read the numbers the model already produces internally — nothing sensitive is re-exposed.',
  },
  {
    icon: '🧾',
    title: 'An audit trail by construction',
    body:
      'Every commit is a new, timestamped, immutable record. History is append-only, which is exactly what an auditor wants — D/R/N tells you what was dropped, what stayed and what was new.',
  },
  {
    icon: '💻',
    title: 'No GPU-shaped bill',
    body:
      'State operations are bit operations on a fixed plate. The platform proof-of-concept runs on commodity CPU hardware with no GPU required.',
  },
  {
    icon: '🔁',
    title: 'Fail-safe by construction',
    body:
      'An uncommitted unit can be discarded and rebuilt from the last committed state, because re-processing is idempotent. A crash costs a pop, not a rebuild.',
  },
  {
    icon: '📖',
    title: 'Open core',
    body:
      'The measurement layer and core crates are MIT-licensed: adoption, reference implementations, standard-setting. Enterprise guarantees, packaging and support are where the commercial offering sits.',
  },
];

/* ------------------------------------------------------------------ */
/*  Shared disclaimer text (single source of truth)                   */
/* ------------------------------------------------------------------ */

export const CLINICAL_DISCLAIMER =
  'Clinical and diagnostic outputs are research demonstrations for partner evaluation. They are not a medical device and do not replace clinician review.';
